import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowLeft, CalendarDays, CheckCircle2, Tag, Award, FileText, Mail, MessageCircle } from "lucide-react";
import { downloadFile } from "../lib/download";
import { toast, Toaster } from "sonner";
import { slotClass } from "../lib/slotColors";
import { gameTypeLabel } from "../lib/facilities";
import { isContiguous } from "../lib/slotTime";
import { localYMD, currentHHMM, today as todayYMD, ddmmyyyy, weekdayLong } from "../lib/dates";
import MultiDateSlotGrid from "../components/MultiDateSlotGrid";
import MapEmbed from "../components/MapEmbed";
import { openRazorpayCheckout } from "../lib/razorpay";

function fmtDate(d) {
    return localYMD(d);
}

export default function Book() {
    const { turfId, centreId, facilityId } = useParams();
    const navigate = useNavigate();
    const { user, refresh: refreshUser } = useAuth();
    const [turf, setTurf] = useState(null);          // legacy mode
    const [facility, setFacility] = useState(null);  // new mode
    const [centre, setCentre] = useState(null);
    const [date, setDate] = useState(fmtDate(new Date()));
    const [slotsData, setSlotsData] = useState(null);
    const [selected, setSelected] = useState([]);
    const [step, setStep] = useState(1);
    const [coupon, setCoupon] = useState("");
    const [availableCoupons, setAvailableCoupons] = useState([]);
    const [discount, setDiscount] = useState(0);
    const [paymentOption, setPaymentOption] = useState("full");
    const [submitting, setSubmitting] = useState(false);
    const [confirmed, setConfirmed] = useState(null);
    const [useLoyalty, setUseLoyalty] = useState(false);

    const isFacilityMode = !!(centreId && facilityId);

    // Load centre + facility (new mode) OR centre + turf (legacy)
    useEffect(() => {
        const load = async () => {
            if (isFacilityMode) {
                const { data: full } = await api.get(`/centres/${centreId}`);
                setCentre(full);
                const f = (full.facilities || []).find(x => x.id === facilityId);
                setFacility(f || null);
            } else {
                const { data: centres } = await api.get("/centres");
                for (const c of centres) {
                    const { data: full } = await api.get(`/centres/${c.id}`);
                    const t = full.turfs.find(x => x.id === turfId);
                    if (t) { setCentre(full); setTurf(t); break; }
                }
            }
        };
        load();
    }, [turfId, centreId, facilityId, isFacilityMode]);

    // Load slots — do NOT reset `selected` here; that's handled in handleMultiGridSelection
    // when the user actually changes date. Resetting on every fetch wiped the user's first
    // click (the centre/facility async load completes ~2s after the initial click).
    useEffect(() => {
        if (isFacilityMode) {
            if (!centre || !facility) return;
            api.get("/slots", { params: { centre_id: centre.id, facility_id: facility.id, booking_date: date } })
                .then(({ data }) => setSlotsData(data));
        } else {
            if (!turf) return;
            api.get("/slots", { params: { turf_id: turf.id, booking_date: date } })
                .then(({ data }) => setSlotsData(data));
        }
    }, [turf, centre, facility, date, isFacilityMode]);

    // Iter 32 / U1 — fetch the catalogue of coupons available to this user.
    // Returns expired ones too (with `is_expired:true`) so the FE can render them as disabled "Expired" chips.
    useEffect(() => {
        api.get("/coupons/available").then(({ data }) => setAvailableCoupons(data || []))
            .catch(() => setAvailableCoupons([]));
    }, []);

    const toggleSlot = (t, booked) => {
        if (booked) return;
        setSelected((prev) => {
            const next = prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t].sort();
            if (!isContiguous(next)) {
                toast.error("Please select continuous slots (no gaps allowed)");
                return prev;
            }
            return next;
        });
    };

    const handleMultiGridSelection = (d, times) => {
        if (!isContiguous(times)) {
            toast.error("Please select continuous slots (no gaps allowed)");
            return;
        }
        setDate(d);
        setSelected(times);
    };

    // Always reason about the chronological boundary, never trust insertion order.
    const sortedSelected = useMemo(() => [...selected].sort(), [selected]);
    const startTime = sortedSelected[0];
    const endTime = sortedSelected.length ? addMinToTime(sortedSelected[sortedSelected.length - 1], 30) : null;
    const durationMin = sortedSelected.length * 30;

    const [appSettings, setAppSettings] = useState({ gst_enabled: false, gst_percent: 0 });

    useEffect(() => {
        api.get("/app-settings").then(({ data }) => setAppSettings(data)).catch(() => {});
    }, []);

    // Subtotal honours each slot's per-30-min price (peak/off-peak + admin overrides)
    const subtotal = useMemo(() => {
        if (!slotsData) return 0;
        const selSet = new Set(selected);
        return (slotsData.slots || [])
            .filter(s => selSet.has(s.time))
            .reduce((sum, s) => sum + (s.price || slotsData.half_hour_price || 0), 0);
    }, [slotsData, selected]);
    const userPts = user?.loyalty_points || 0;
    const maxLoyalty = userPts >= 500 ? Math.min(Math.floor(userPts * 0.20), subtotal) : 0;
    const loyaltyApplied = useLoyalty && !coupon ? maxLoyalty : 0;
    const effectiveDiscount = loyaltyApplied > 0 ? loyaltyApplied : discount;
    const preTax = Math.max(0, subtotal - effectiveDiscount);
    const gstAmount = appSettings.gst_enabled && appSettings.gst_percent > 0
        ? Math.round(preTax * appSettings.gst_percent / 100)
        : 0;
    const total = preTax + gstAmount;
    const paidNow = paymentOption === "full" ? total : Math.floor(total / 2);
    const balance = total - paidNow;

    const applyCoupon = async () => {
        if (!coupon) return;
        if (useLoyalty) { toast.error("Disable loyalty redemption to use a coupon"); return; }
        try {
            const { data } = await api.post("/coupons/validate", { code: coupon, amount: subtotal });
            setDiscount(data.discount);
            toast.success(`Coupon applied: -₹${data.discount}`);
        } catch (e) {
            setDiscount(0);
            toast.error(e.response?.data?.detail || "Invalid coupon");
        }
    };

    const goToCheckout = () => {
        if (selected.length < 2) { toast.error("Pick at least 2 consecutive slots (1 hour minimum)"); return; }
        setStep(2);
    };

    const confirmBooking = async () => {
        setSubmitting(true);
        try {
            const payload = {
                centre_id: centre.id,
                booking_date: date,
                slots: selected,
                payment_option: paymentOption,
                coupon_code: !useLoyalty && discount > 0 ? coupon : null,
                loyalty_points: useLoyalty ? loyaltyApplied : 0,
            };
            if (isFacilityMode) payload.facility_id = facility.id;
            else payload.turf_id = turf.id;

            // 1) Check whether the server has Razorpay wired up
            const { data: cfg } = await api.get("/payments/razorpay/config");

            // 2) If yes, open Razorpay first, then send the booking with the verified signature
            if (cfg?.configured) {
                const { data: order } = await api.post("/payments/razorpay/create-order", payload);
                let paid;
                try {
                    paid = await openRazorpayCheckout({
                        keyId: order.key_id,
                        orderId: order.order_id,
                        amount: order.amount,
                        currency: order.currency,
                        user,
                        name: "Drive or Kick",
                        description: `${centre.name} · ${date} · ${selected[0]}–${selected[selected.length - 1]}`,
                    });
                } catch (err) {
                    toast.error(err?.message || "Payment cancelled");
                    setSubmitting(false);
                    return;
                }
                payload.razorpay_order_id = paid.orderId;
                payload.razorpay_payment_id = paid.paymentId;
                payload.razorpay_signature = paid.signature;
            }

            const { data } = await api.post("/bookings", payload);
            setConfirmed(data);
            setStep(3);
            await refreshUser();
            toast.success("Booking confirmed!");
        } catch (e) {
            const d = e.response?.data?.detail;
            const msg = typeof d === "string" ? d : (e.message || "Booking failed");
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const dateOptions = useMemo(() => {
        const arr = [];
        const today = new Date();
        for (let i = 0; i < 14; i++) {
            const d = new Date(today); d.setDate(today.getDate() + i);
            arr.push({ value: fmtDate(d), label: d.toLocaleDateString("en", { weekday: "short" }), day: d.getDate(), month: d.toLocaleDateString("en", { month: "short" }) });
        }
        return arr;
    }, []);

    if ((!isFacilityMode && !turf) || !centre || (isFacilityMode && !facility)) {
        return <div className="p-8 text-center text-slate-400">Loading...</div>;
    }

    const headerTitle = isFacilityMode ? facility.name : `${turf.name} · ${turf.sport}`;
    const headerSubtitle = isFacilityMode ? gameTypeLabel(facility.game_type) : "";
    const hourlyPrice = isFacilityMode ? facility.hourly_price : turf.hourly_price;

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
            <Toaster theme="dark" richColors position="top-center" />
            <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-4 text-sm" data-testid="book-back-btn">
                <ArrowLeft className="w-4 h-4" /> Back
            </button>

            {/* Stepper */}
            <div className="flex items-center gap-2 mb-6 text-xs uppercase tracking-widest">
                {["Slots", "Checkout", "Done"].map((label, idx) => (
                    <div key={label} className="flex items-center gap-2">
                        <div className={`w-7 h-7 flex items-center justify-center rounded-full font-bold border ${
                            step > idx + 1 ? "bg-[#00FF66] text-black border-[#00FF66]" :
                            step === idx + 1 ? "bg-slate-900 text-[#00FF66] border-[#00FF66]" :
                            "bg-slate-900 text-slate-500 border-slate-700"
                        }`}>{idx + 1}</div>
                        <span className={step === idx + 1 ? "text-white font-bold" : "text-slate-500"}>{label}</span>
                        {idx < 2 && <span className="w-8 h-px bg-slate-800 ml-2" />}
                    </div>
                ))}
            </div>

            {/* Header */}
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 mb-6" data-testid="book-header">
                <div className="text-xs text-slate-400 uppercase tracking-widest">{centre.name}</div>
                <div className="font-display text-xl font-bold text-white uppercase">{headerTitle}</div>
                {headerSubtitle && <div className="text-[11px] text-slate-400 mt-0.5">{headerSubtitle}</div>}
                <div className="text-xs text-[#00FF66] mt-1 font-bold">₹{hourlyPrice}/hour</div>
            </div>

            {step === 1 && (
                <>
                    {isFacilityMode ? (
                        <>
                            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                <div className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-slate-400">
                                    <CalendarDays className="w-4 h-4" /> Pick continuous slots from any column · 30 min each
                                </div>
                                <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-400">
                                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-white border border-slate-300 rounded-sm" />Free</span>
                                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-sky-400 rounded-sm" />Selected</span>
                                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-[#00FF66]/40 border border-[#00FF66]/60 rounded-sm" />Booked</span>
                                    <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-slate-600/60 border border-slate-500 rounded-sm" />Blocked</span>
                                </div>
                            </div>
                            <MultiDateSlotGrid
                                centreId={centre.id}
                                facilityId={facility.id}
                                selectedDate={date}
                                selectedTimes={selected}
                                onSelectionChange={handleMultiGridSelection}
                            />
                        </>
                    ) : (
                        <>
                            <div className="mb-5">
                                <div className="flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-3">
                                    <CalendarDays className="w-4 h-4" /> Pick a date
                                </div>
                                <div className="flex gap-2 overflow-x-auto pb-2">
                                    {dateOptions.map((d) => (
                                        <button key={d.value} onClick={() => setDate(d.value)}
                                            data-testid={`date-${d.value}`}
                                            className={`flex-shrink-0 w-16 py-3 rounded-lg border text-center transition-colors ${
                                                date === d.value ? "bg-[#00FF66] text-black border-[#00FF66]" : "bg-slate-900 text-slate-200 border-slate-800 hover:border-[#00FF66]/50"
                                            }`}>
                                            <div className="text-[10px] uppercase font-bold tracking-widest">{d.label}</div>
                                            <div className="font-display text-2xl font-bold leading-none mt-1">{d.day}</div>
                                            <div className="text-[10px] uppercase mt-1">{d.month}</div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Available slots (30 min)</div>
                                    <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-400">
                                        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-white border border-slate-300 rounded-sm" />Free</span>
                                        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-sky-400 rounded-sm" />Selected</span>
                                        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-[#00FF66]/40 border border-[#00FF66]/60 rounded-sm" />Booked</span>
                                        <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-slate-600/60 border border-slate-500 rounded-sm" />Blocked</span>
                                    </div>
                                </div>
                                {slotsData ? (
                                    <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
                                        {slotsData.slots.map((s) => {
                                            const isSel = selected.includes(s.time);
                                            const cls = slotClass({ booked: s.booked && !s.is_block, isBlock: s.is_block, selected: isSel });
                                            return (
                                                <button key={s.time} onClick={() => toggleSlot(s.time, s.booked)} disabled={s.booked}
                                                    data-testid={`slot-${s.time}`}
                                                    className={`h-12 rounded text-sm font-bold transition-all border ${cls}`}>{to12h(s.time)}</button>
                                            );
                                        })}
                                    </div>
                                ) : <div className="text-slate-500 text-sm">Loading slots...</div>}
                            </div>
                        </>
                    )}

                    <div className="sticky bottom-0 md:static mt-6 -mx-4 sm:mx-0 p-4 sm:p-0 bg-slate-950/85 sm:bg-transparent backdrop-blur-xl sm:backdrop-blur-0 border-t border-slate-800 sm:border-0">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <div className="text-xs text-slate-400 uppercase tracking-widest">
                                    {sortedSelected.length} slot{sortedSelected.length !== 1 ? "s" : ""} · {durationMin} min
                                    {date ? <> · <span className="font-bold text-white normal-case tracking-normal">{weekdayLong(date)}</span>, {ddmmyyyy(date)}</> : null}
                                    {startTime ? ` · ${to12h(startTime)}–${to12h(endTime)}` : ""}
                                </div>
                                <div className="font-display text-2xl font-bold text-white">₹{subtotal}</div>
                            </div>
                            <button onClick={goToCheckout} disabled={selected.length < 2}
                                data-testid="goto-checkout-btn"
                                className="px-6 h-12 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52] active:scale-95 transition disabled:opacity-40 disabled:cursor-not-allowed glow-green">
                                Continue
                            </button>
                        </div>
                    </div>
                </>
            )}

            {step === 2 && (
                <div className="space-y-4">
                    <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
                        <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-3">Booking summary</div>
                        <div className="space-y-2 text-sm">
                            <Row label="Centre" value={centre.name} />
                            <Row label="Facility" value={headerTitle} />
                            <Row label="Date" value={date ? <><span className="font-bold text-white">{weekdayLong(date)}</span>{`, ${ddmmyyyy(date)}`}</> : "—"} />
                            <Row label="Time" value={startTime ? `${to12h(startTime)} – ${to12h(endTime)}` : "—"} />
                            <Row label="Duration" value={`${durationMin} minutes`} />
                        </div>
                    </div>

                    <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
                        <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-3 flex items-center gap-2">
                            <Tag className="w-4 h-4" /> Apply coupon
                        </div>
                        {availableCoupons.length > 0 && (
                            <div className="mb-3 flex flex-wrap gap-2" data-testid="available-coupons">
                                {availableCoupons.map(c => {
                                    const expired = c.is_expired || c.is_exhausted || !c.active;
                                    const valLabel = c.discount_type === "percent" ? `${c.discount_value}% off` : `₹${c.discount_value} off`;
                                    return (
                                        <button key={c.code} type="button"
                                            disabled={expired || useLoyalty}
                                            onClick={() => setCoupon(c.code)}
                                            data-testid={`coupon-chip-${c.code}`}
                                            title={c.description || ""}
                                            className={`text-[10px] uppercase font-bold tracking-widest px-2.5 py-1 rounded border ${expired
                                                ? "border-slate-700 text-slate-500 cursor-not-allowed"
                                                : "border-[#00FF66]/40 text-[#00FF66] hover:bg-[#00FF66]/10"}`}>
                                            <span>{c.code}</span>
                                            <span className="mx-1 opacity-50">·</span>
                                            <span className={expired ? "" : "text-slate-300"}>{valLabel}</span>
                                            {expired && <span className="ml-1.5 text-red-400">Expired</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        <div className="flex gap-2">
                            <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} disabled={useLoyalty}
                                placeholder="WELCOME10 / FLAT200" data-testid="coupon-input"
                                className="flex-1 h-11 px-4 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66] uppercase disabled:opacity-50" />
                            <button onClick={applyCoupon} disabled={useLoyalty} data-testid="apply-coupon-btn"
                                className="px-4 h-11 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white font-bold uppercase text-xs tracking-widest rounded disabled:opacity-50">
                                Apply
                            </button>
                        </div>
                        {discount > 0 && !useLoyalty && <div className="text-[#00FF66] text-xs mt-2 font-bold">✓ Saved ₹{discount}</div>}
                    </div>

                    {(user?.loyalty_points || 0) > 0 && (
                        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5" data-testid="loyalty-block">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 inline-flex items-center gap-2">
                                        <Award className="w-4 h-4 text-[#00FF66]" /> Loyalty points
                                    </div>
                                    <div className="text-sm text-slate-300 mt-1">
                                        You have <span className="text-[#00FF66] font-bold">{user.loyalty_points} pts</span>
                                        {userPts >= 500
                                            ? <> · Up to <span className="text-white font-bold">₹{maxLoyalty}</span> off (20% of balance)</>
                                            : <> · Need {500 - userPts} more pts to redeem (min 500)</>}
                                    </div>
                                    <div className="text-[11px] text-slate-500 mt-1">100 pts credited after each completed booking. Cannot be combined with a coupon.</div>
                                </div>
                                <label className="inline-flex items-center cursor-pointer">
                                    <input type="checkbox" checked={useLoyalty} onChange={(e) => { setUseLoyalty(e.target.checked); if (e.target.checked) { setCoupon(""); setDiscount(0); } }}
                                        className="sr-only peer" data-testid="loyalty-toggle" disabled={maxLoyalty === 0} />
                                    <div className="w-11 h-6 bg-slate-700 peer-checked:bg-[#00FF66] rounded-full transition-colors relative">
                                        <span className="absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform peer-checked:translate-x-5" />
                                    </div>
                                </label>
                            </div>
                            {useLoyalty && loyaltyApplied > 0 && <div className="text-[#00FF66] text-xs mt-2 font-bold">✓ Redeeming {loyaltyApplied} pts (-₹{loyaltyApplied})</div>}
                        </div>
                    )}

                    <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
                        <div className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mb-3">Payment</div>
                        <div className="grid grid-cols-2 gap-2">
                            {[
                                { v: "full", t: "Pay in Full", s: `₹${total}` },
                                { v: "advance", t: "Pay Advance 50%", s: `₹${Math.floor(total / 2)} now` },
                            ].map(opt => (
                                <button key={opt.v} onClick={() => setPaymentOption(opt.v)}
                                    data-testid={`pay-${opt.v}-btn`}
                                    className={`p-4 text-left rounded border transition ${
                                        paymentOption === opt.v ? "border-[#00FF66] bg-[#00FF66]/10" : "border-slate-700 bg-slate-950"
                                    }`}>
                                    <div className="font-bold text-white text-sm">{opt.t}</div>
                                    <div className="text-xs text-slate-400 mt-1">{opt.s}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-2">
                        <Row label="Subtotal" value={`₹${subtotal}`} />
                        {effectiveDiscount > 0 && <Row label={useLoyalty ? `Loyalty (${loyaltyApplied} pts)` : "Coupon discount"} value={`-₹${effectiveDiscount}`} valueClass="text-[#00FF66]" />}
                        {gstAmount > 0 && <Row label={`GST @ ${appSettings.gst_percent}%`} value={`+₹${gstAmount}`} valueClass="text-amber-400" testid="row-gst" />}
                        <div className="border-t border-slate-800 my-2" />
                        <Row label="Total" value={`₹${total}`} bold testid="row-total" />
                        <Row label="Paying now" value={`₹${paidNow}`} valueClass="text-[#00FF66] font-bold" />
                        {balance > 0 && <Row label="Balance at venue" value={`₹${balance}`} valueClass="text-amber-400" />}
                    </div>

                    <button onClick={confirmBooking} disabled={submitting}
                        data-testid="confirm-booking-btn"
                        className="w-full h-13 py-3 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52] active:scale-95 transition disabled:opacity-50 glow-green">
                        {submitting ? "Processing payment..." : `Pay ₹${paidNow}`}
                    </button>
                    <button onClick={() => setStep(1)} className="w-full h-10 text-slate-400 hover:text-white text-sm" data-testid="back-to-slots-btn">
                        ← Back to slots
                    </button>
                </div>
            )}

            {step === 3 && confirmed && (
                <div className="text-center py-8">
                    <div className="w-20 h-20 mx-auto rounded-full bg-[#00FF66]/15 border-2 border-[#00FF66] flex items-center justify-center glow-green-strong mb-6">
                        <CheckCircle2 className="w-10 h-10 text-[#00FF66]" />
                    </div>
                    <h2 className="font-display text-3xl font-bold uppercase text-white">Booking Confirmed!</h2>
                    <p className="text-slate-400 mt-2 text-sm">Your turf is locked in. Show this code at the venue.</p>
                    <div className="mt-6 inline-block px-6 py-3 bg-slate-900 border-2 border-[#00FF66] rounded font-display font-bold text-2xl tracking-widest text-[#00FF66]" data-testid="booking-code">
                        {confirmed.booking_code}
                    </div>
                    <div className="mt-6 max-w-sm mx-auto bg-slate-900 border border-slate-800 rounded-lg p-5 text-left space-y-2 text-sm">
                        <Row label="Centre" value={confirmed.centre_name} />
                        <Row label="Facility" value={confirmed.facility_name || confirmed.turf_name} />
                        <Row label="Date" value={<><span className="font-bold text-white">{weekdayLong(confirmed.booking_date)}</span>{`, ${ddmmyyyy(confirmed.booking_date)}`}</>} />
                        <Row label="Time" value={`${to12h(confirmed.start_time)} – ${to12h(confirmed.end_time)}`} />
                        <Row label="Duration" value={`${confirmed.duration_min} minutes`} />
                        <Row label="Paid" value={`₹${confirmed.paid_amount}`} valueClass="text-[#00FF66] font-bold" />
                        {confirmed.balance_due > 0 && <Row label="Balance at venue" value={`₹${confirmed.balance_due}`} valueClass="text-amber-400" />}
                    </div>

                    {/* MOD 24 — Map (manual embed URL) on confirmation */}
                    {centre?.map_embed_url && (
                        <div className="mt-6 max-w-sm mx-auto" data-testid="confirmation-map">
                            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-2 text-left">Venue Location</div>
                            <MapEmbed
                                url={extractEmbedSrc(centre.map_embed_url)}
                                height={200}
                                label={centre.name}
                                showDirections={false}
                            />
                        </div>
                    )}

                    <div className="mt-6 max-w-sm mx-auto bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid="invoice-actions">
                        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-400 mb-2 inline-flex items-center gap-1.5">
                            <FileText className="w-3.5 h-3.5 text-[#00FF66]" /> Tax Invoice
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            <button onClick={async () => {
                                try {
                                    await downloadFile(`/bookings/${confirmed.id}/invoice.pdf`, `invoice_${confirmed.booking_code}.pdf`);
                                } catch (e) { toast.error("Could not download invoice"); }
                            }} data-testid="invoice-download-btn"
                                className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 bg-slate-950 border border-slate-700 hover:border-[#00FF66]/60 rounded text-xs font-bold uppercase tracking-widest text-white">
                                <FileText className="w-3.5 h-3.5" /> Download
                            </button>
                            <button onClick={async () => {
                                try {
                                    const { data } = await api.post(`/bookings/${confirmed.id}/send-invoice`, null, { params: { channel: "email" } });
                                    toast.success(`Invoice emailed to ${data.to}`);
                                } catch (e) { toast.error(e.response?.data?.detail || "Email failed"); }
                            }} data-testid="invoice-email-btn"
                                className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 bg-slate-950 border border-slate-700 hover:border-[#00FF66]/60 rounded text-xs font-bold uppercase tracking-widest text-white">
                                <Mail className="w-3.5 h-3.5" /> Email
                            </button>
                            <button onClick={async () => {
                                try {
                                    const { data } = await api.post(`/bookings/${confirmed.id}/send-invoice`, null, { params: { channel: "whatsapp" } });
                                    toast.success(`Invoice sent to ${data.to}`);
                                } catch (e) { toast.error(e.response?.data?.detail || "WhatsApp send failed"); }
                            }} data-testid="invoice-whatsapp-btn"
                                className="flex-1 inline-flex items-center justify-center gap-1.5 h-10 bg-[#00FF66]/10 border border-[#00FF66]/40 hover:bg-[#00FF66]/20 rounded text-xs font-bold uppercase tracking-widest text-[#00FF66]">
                                <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                            </button>
                        </div>
                    </div>

                    <div className="mt-8 flex gap-3 justify-center">
                        <button onClick={() => navigate("/bookings")}
                            data-testid="view-bookings-btn"
                            className="px-6 h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52]">
                            View My Bookings
                        </button>
                        <button onClick={() => navigate("/")}
                            className="px-6 h-11 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white font-bold uppercase tracking-widest text-xs rounded">
                            Home
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function Row({ label, value, valueClass = "", bold = false, testid }) {
    return (
        <div className="flex justify-between text-sm" data-testid={testid}>
            <span className="text-slate-400">{label}</span>
            <span className={`${bold ? "font-display font-bold text-lg text-white" : "text-white"} ${valueClass}`}>{value}</span>
        </div>
    );
}

function to12h(t) {
    if (!t) return "";
    const [hStr, mStr] = String(t).split(":");
    const h = parseInt(hStr);
    const m = parseInt(mStr) || 0;
    const ap = h >= 12 ? "PM" : "AM";
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return m === 0 ? `${h12} ${ap}` : `${h12}:${String(m).padStart(2, "0")} ${ap}`;
}

function addMinToTime(t, mins) {
    const [h, m] = t.split(":").map(Number);
    const total = h * 60 + m + mins;
    // Wrap past midnight so 23:30+30 → "00:00" (not "24:00", which would render as "12 PM" instead of "12 AM").
    const wrapped = ((total % 1440) + 1440) % 1440;
    const hh = String(Math.floor(wrapped / 60)).padStart(2, "0");
    const mm = String(wrapped % 60).padStart(2, "0");
    return `${hh}:${mm}`;
}

function extractEmbedSrc(raw) {
    if (!raw) return "";
    const s = raw.trim();
    if (s.startsWith("<iframe")) {
        const m = s.match(/src="([^"]+)"/);
        return m ? m[1] : "";
    }
    return s;
}
